import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Drag your homes left and right in the homes dock (assistants/_dashboard_rail),
// as the sidebar's items drag up and down (nav_reorder_controller). Only your
// own homes move ([data-dashboard-id]); a home shared with you and Zen keep
// their places, and nothing drops past them. The order saves on drop
// (PATCH /dashboards/order) and the header's switcher follows it on the next
// page. Desktop only: a coarse pointer gets no drag, as in the sidebar.
const ITEM = "a[data-dashboard-id]"

export default class extends Controller {
    static values = { url: String }

    connect() {
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return

        this._onDragOver = (event) => this._dragOver(event)
        this._onDrop = (event) => event.preventDefault()
        this.element.addEventListener("dragover", this._onDragOver)
        this.element.addEventListener("drop", this._onDrop)
        this._items().forEach((item) => {
            item.setAttribute("draggable", "true")
            item.addEventListener("dragstart", (event) => this._dragStart(event, item))
            item.addEventListener("dragend", () => this._dragEnd())
        })
    }

    // The ⋯ menu's "New dashboard": the header switcher's own name-it row, opened.
    newDashboard(event) {
        event?.preventDefault()
        const picker = document.querySelector(".dashboard-picker")
        if (!picker) return
        const trigger = picker.querySelector(".dashboard-picker-trigger")
        if (trigger?.getAttribute("aria-expanded") !== "true") trigger?.click()
        setTimeout(() => picker.querySelector("[data-dashboard-picker-target=newToggle]")?.click(), 0)
    }

    disconnect() {
        this.element.removeEventListener("dragover", this._onDragOver)
        this.element.removeEventListener("drop", this._onDrop)
    }

    _items() {
        return Array.from(this.element.querySelectorAll(ITEM))
    }

    _dragStart(event, item) {
        this._dragging = item
        this._before = this._ids().join(",")
        item.classList.add("is-dragging")
        this.element.classList.add("is-reordering")
        event.dataTransfer.effectAllowed = "move"
        try { event.dataTransfer.setData("text/plain", item.dataset.dashboardId) } catch { /* old browsers */ }
    }

    _dragOver(event) {
        if (!this._dragging) return
        const over = event.target.closest(ITEM)
        if (!over || !this.element.contains(over)) return
        event.preventDefault()
        if (over === this._dragging) return
        const box = over.getBoundingClientRect()
        const before = event.clientX < box.left + box.width / 2
        over.parentNode.insertBefore(this._dragging, before ? over : over.nextSibling)
    }

    _dragEnd() {
        if (!this._dragging) return
        this._dragging.classList.remove("is-dragging")
        this.element.classList.remove("is-reordering")
        this._dragging = null
        const ids = this._ids()
        if (ids.join(",") === this._before) return
        fetch(this.urlValue, {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ ids })
        }).catch(() => {})
    }

    _ids() {
        return this._items().map((item) => item.dataset.dashboardId)
    }
}
